import React from 'react';

const formatCurrency = (value) => `$${Number(value || 0).toFixed(2)}`;

const roundCurrency = (value) => Math.round((Number(value || 0) + Number.EPSILON) * 100) / 100;

const getCategoryGroupKey = (category, rate) => {
  const normalized = String(category || '').toLowerCase().replace(/[\s_-]/g, '');
  if (normalized.includes('zero')) return 'zeroRated';
  if (normalized.includes('exempt') || normalized.includes('custom')) return 'exemptCustom';
  if (normalized.includes('standard') || Number(rate) > 0) return 'standardRated';
  return 'exemptCustom';
};

const shouldShowGroup = (group) => {
  const key = String(group?.key || '').toLowerCase();
  const inclusiveTotal = Number(group?.inclusiveTotal || 0);
  const gctIncluded = Number(group?.gctIncluded || 0);

  if (key.includes('zero') || key.includes('exempt') || key.includes('custom')) {
    return inclusiveTotal > 0;
  }

  return gctIncluded > 0;
};

const normalizeSummaryForDisplay = (summary) => {
  if (!summary) return null;

  if (summary.groups && summary.totals) {
    return {
      supermarketGctRegistered: summary.supermarketGctRegistered === true,
      totalGctIncluded: summary.totals.gctIncluded,
      groups: [
        summary.groups.standardRated,
        summary.groups.zeroRated,
        summary.groups.exemptCustom,
      ].filter(Boolean).map((group) => ({
        key: group.category,
        label: group.label,
        inclusiveTotal: group.inclusiveTotal,
        gctIncluded: group.gctIncluded,
      })).filter(shouldShowGroup),
    };
  }

  const grouped = {
    standardRated: { key: 'standard_rated', label: 'Standard-rated', inclusiveTotal: 0, gctIncluded: 0 },
    zeroRated: { key: 'zero_rated', label: 'Zero-rated', inclusiveTotal: 0, gctIncluded: 0 },
    exemptCustom: { key: 'exempt_custom', label: 'Exempt/custom', inclusiveTotal: 0, gctIncluded: 0 },
  };

  (summary.breakdownByCategory || []).forEach((category) => {
    const groupKey = getCategoryGroupKey(category.taxCategory, category.taxRate);
    grouped[groupKey].inclusiveTotal = roundCurrency(
      grouped[groupKey].inclusiveTotal + Number(category.taxInclusiveTotal || 0)
    );
    grouped[groupKey].gctIncluded = roundCurrency(
      grouped[groupKey].gctIncluded + Number(category.gctIncludedTotal || 0)
    );
  });

  return {
    supermarketGctRegistered: summary.supermarketGctRegistered === true,
    totalGctIncluded: summary.gctIncludedTotal,
    groups: Object.values(grouped).filter(shouldShowGroup),
  };
};

export default function GctSummaryBreakdown({ taxSummary, className = '', compact = false }) {
  const displaySummary = normalizeSummaryForDisplay(taxSummary);

  if (
    !displaySummary ||
    !displaySummary.supermarketGctRegistered ||
    displaySummary.groups.length === 0
  ) {
    return null;
  }

  return (
    <div className={`gct-summary ${className}`}>
      <div className="d-flex justify-content-between align-items-center">
        <strong>GCT included</strong>
        <span className="fw-semibold">{formatCurrency(displaySummary.totalGctIncluded)}</span>
      </div>
      <div className="text-muted small mb-1">Customer prices remain tax-inclusive.</div>
      {!compact && (
        <div className="small">
          {displaySummary.groups.map((group) => (
            <div key={group.key} className="d-flex justify-content-between">
              <span>{group.label}</span>
              <span>
                {formatCurrency(group.inclusiveTotal)}
                {group.gctIncluded > 0 ? ` incl. ${formatCurrency(group.gctIncluded)} GCT` : ' incl. $0.00 GCT'}
              </span>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
